import { useEffect, useState } from "react";
import {
  X,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  RotateCcw,
  User as UserIcon,
  Loader2,
  AlertTriangle,
  MessageSquare,
} from "lucide-react";
import moment from "moment";
import { getOfferHistory } from "../../../api/propertyManagement";

/**
 * OfferTimelineModal — agent-facing chronological view of every event on an offer.
 *
 * Powered by `GET /api/property-management/offers/{id}/history/` which returns the
 * full (no-filter) audit log. Events are visually grouped by actor (agent, buyer,
 * vendor, system) so the agent can quickly trace the negotiation back-and-forth.
 *
 * Props:
 *   isOpen, onClose, offer
 */

const ICON_BY_TYPE = {
  SUBMITTED: { Icon: Send, tone: "blue" },
  SENT_TO_VENDOR: { Icon: Send, tone: "amber" },
  VENDOR_VIEWED: { Icon: UserIcon, tone: "gray" },
  VENDOR_ACCEPTED: { Icon: CheckCircle2, tone: "emerald" },
  VENDOR_DECLINED: { Icon: XCircle, tone: "rose" },
  VENDOR_COUNTERED: { Icon: RotateCcw, tone: "violet" },
  SENT_TO_BUYER: { Icon: Send, tone: "emerald" },
  BUYER_VIEWED: { Icon: UserIcon, tone: "gray" },
  BUYER_ACCEPTED_COUNTER: { Icon: CheckCircle2, tone: "emerald" },
  BUYER_COUNTERED: { Icon: RotateCcw, tone: "violet" },
  BUYER_WITHDREW: { Icon: XCircle, tone: "gray" },
  AGENT_NOTE: { Icon: MessageSquare, tone: "cyan" },
  MUTUAL_ACCEPTANCE: { Icon: CheckCircle2, tone: "emerald" },
  STATUS_CHANGED: { Icon: Clock, tone: "gray" },
};

const TONE = {
  blue: { bg: "bg-blue-50", border: "border-blue-100", text: "text-blue-700" },
  amber: { bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-700" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-100", text: "text-emerald-700" },
  rose: { bg: "bg-rose-50", border: "border-rose-100", text: "text-rose-700" },
  violet: { bg: "bg-violet-50", border: "border-violet-100", text: "text-violet-700" },
  gray: { bg: "bg-gray-50", border: "border-gray-100", text: "text-gray-600" },
  cyan: { bg: "bg-[#55C5D0]/5", border: "border-[#55C5D0]/20", text: "text-[#3a939c]" },
};

const ACTOR_LABEL = {
  agent: "Agent",
  buyer: "Buyer",
  vendor: "Vendor",
  system: "System",
};

const formatMoney = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return `$${n.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
};

export default function OfferTimelineModal({ isOpen, onClose, offer }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !offer?.id) return;
    setLoading(true);
    setError(null);
    getOfferHistory(offer.id)
      .then((res) => setEvents(res?.data?.results || []))
      .catch((err) => {
        console.error("Failed to load offer history:", err);
        setError("Could not load history.");
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, offer?.id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] grid place-items-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
              Offer Timeline
            </p>
            <h2 className="text-lg font-bold text-gray-900 mt-1">
              {offer?.reference_number || "Offer"} — {events.length} event
              {events.length === 1 ? "" : "s"}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Full chronology of every action across agent, buyer and vendor.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1 -mr-1"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-rose-600 flex flex-col items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              {error}
            </div>
          ) : events.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              No events recorded yet.
            </div>
          ) : (
            <ol className="relative border-l-2 border-gray-100 ml-3 space-y-4">
              {events.map((ev) => {
                const { Icon, tone } = ICON_BY_TYPE[ev.event_type] || {
                  Icon: Clock,
                  tone: "gray",
                };
                const toneCls = TONE[tone] || TONE.gray;
                const amountStr = formatMoney(ev.amount);
                return (
                  <li key={ev.id} className="ml-6">
                    <span
                      className={`absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-white ${toneCls.bg} ${toneCls.text}`}
                    >
                      <Icon size={12} />
                    </span>
                    <div
                      className={`rounded-xl border p-3 ${toneCls.bg} ${toneCls.border}`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className={`text-sm font-bold ${toneCls.text}`}>
                          {ev.event_label}
                        </p>
                        <span className="text-[10px] uppercase tracking-widest text-gray-500">
                          {ev.created_at
                            ? moment(ev.created_at).format(
                                "DD MMM YYYY · HH:mm",
                              )
                            : ""}
                        </span>
                      </div>
                      <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-0.5">
                        {ACTOR_LABEL[ev.actor] || ev.actor}
                        {ev.actor_label ? ` · ${ev.actor_label}` : ""}
                      </div>
                      {amountStr && (
                        <p className="text-sm font-bold text-emerald-700 mt-2">
                          {amountStr}
                        </p>
                      )}
                      {ev.message && (
                        <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">
                          {ev.message}
                        </p>
                      )}
                      {ev.visibility && ev.visibility !== "all" && (
                        <p className="text-[10px] text-gray-400 mt-2">
                          Visibility: {ev.visibility.replaceAll("_", " ")}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg border-2 border-gray-200 text-gray-700 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
